// @flow
/* eslint react/jsx-no-bind:0 */

import React, { Component } from 'react';
import Button from 'app/common/react/components/growth/unauth/Button/Button';
import CONST from 'app/common/lib/Const';
import i18n from 'app/common/lib/i18n';
import login from 'app/common/react/components/growth/unauth/signup/UserLogin';
import loginUtil from 'app/www-unauth/lib/loginUtil';
import PropTypes from 'prop-types';
import runValidators from 'app/common/react/components/growth/unauth/lib/ValidationUtils';
import TextInputField from 'app/common/react/components/growth/unauth/signup/TextInputField/TextInputField';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import type { LoginCreds } from 'app/packages/pinterest-flow-types/authentication';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { showError } from 'app/common/react/components/growth/unauth/lib/ErrorUtils';
import { MfaCode, Required } from 'app/common/lib/react/validator';

const cannonicalPasswordResetUrl = CONST.GEN.templateConst.settings.PASSWORD_RESET_URL;

const styles = {
  loginForm: {
    borderTop: 'solid 1px #ccc',
    minHeight: '350px',
    padding: '20px 10px',
  },
  loginText: {
    fontSize: '14px',
    margin: '24px 0',
    textAlign: 'left',
  },
  loginTitleContainer: {
    margin: '0 auto 25px',
  },
  loginTitle: {
    fontSize: '28px',
    fontWeight: 'bold',
    letterSpacing: '-1px',
  },
  loginContainer: {
    margin: '0 auto',
    marginTop: '45px',
    width: '268px',
  },
  loginFooterContainer: {
    marginTop: '15px',
    color: '#333',
  },
  signupButton: {
    width: '100%',
  },
};

const pageName = 'loginmfa';

type Props = {|
  container?: string,
  code?: string,
  creds?: ?LoginCreds,
  email: string,
  inputStyleOverrides?: { [string]: string },
  isAccountSwitch?: boolean,
  nextUrlParam?: string,
  onCodeChange: (e: *) => void,
  password: string,
  phoneNumberEnd?: string,
  useGestaltTooltip?: boolean,
|};

type State = {|
  codeValidationError: ?string,
  loading: boolean,
|};

export class LoginMfa extends Component<Props, State> {
  static defaultProps = {
    container: 'unknown',
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      codeValidationError: null,
      loading: false,
    };
    this.codeValidators = [
      Required({
        message: i18n._("You missed a spot! Don't forget to add your verification code."),
      }),
      MfaCode({ message: i18n._("Hmm...that doesn't look like a verification code.") }),
    ];
  }

  componentDidMount() {
    if (
      !this.props.isAccountSwitch &&
      loginUtil.isFullyLoggedIn(this.context.isAuthenticated, this.context.loginState)
    ) {
      loginUtil.handleRedirect(this.props.nextUrlParam);
    }
  }

  _getLocationForLoginRedirect() {
    const fallback =
      window.location.pathname !== cannonicalPasswordResetUrl ? window.location.pathname : '/';
    return this.props.nextUrlParam || fallback;
  }

  codeValidators: Array<any>;

  handleButtonClick(e: SyntheticEvent<>) {
    const error = runValidators(this.props.code, this.codeValidators);
    if (error) {
      this.setState({ codeValidationError: error });
      trackRegisterAction(`unauth.login.error.CLIENT_ERROR.wrongcode.${pageName}`);
    } else {
      this.setState({ codeValidationError: null });
      this.loginUser();
    }
  }

  handleResendCode(e: SyntheticEvent<>) {
    e.preventDefault();
    this.setState({ loading: true });
    let creds = this.props.creds;
    if (creds) {
      creds.mfa_resend = true;
    } else {
      creds = {
        mfa_resend: true,
        username_or_email: this.props.email,
        password: this.props.password,
      };
    }
    const loggingData = {
      container: this.props.container,
      hybridTier: this.context.hybridTier,
      page: this.context.pageType,
    };
    login(creds, loggingData).then(
      () => {
        this.setState({ loading: false });
      },
      (error) => {
        this.setState({ loading: false });
        if (error.message) {
          this.setState({ codeValidationError: error.message });
        } else {
          showError(i18n._('Sorry! Something went wrong on our end.'));
        }
      },
    );
  }

  loginUser(altCreds: ?LoginCreds) {
    this.setState({ loading: true });
    let creds = altCreds || this.props.creds;
    if (creds) {
      creds.mfa_token = this.props.code;
    } else {
      creds = {
        mfa_token: this.props.code,
        username_or_email: this.props.email,
        password: this.props.password,
      };
    }
    const loggingData = {
      container: this.props.container,
      hybridTier: this.context.hybridTier,
      page: this.context.pageType,
    };
    login(creds, loggingData).then(
      () => {
        loginUtil.handleRedirect(this._getLocationForLoginRedirect());
      },
      (error) => {
        if (error.message) {
          this.setState({ codeValidationError: error.message, loading: false });
        } else {
          showError(i18n._('Sorry! Something went wrong on our end.'));
          this.setState({ loading: false });
        }
      },
    );
  }

  render() {
    const loginContainerStyles = styles.loginContainer;
    const buttonText = i18n._('Continue');
    const phoneNumberEnd = this.props.phoneNumberEnd;
    const loginText = phoneNumberEnd
      ? interpolateNamedTemplate(
          i18n._(
            'Please check your phone with the number ending in {{ phoneNumberEnd }} ' +
              'for a code and enter it in the field below to log in.',
          ),
          {
            phoneNumberEnd: <strong key="phone">{phoneNumberEnd}</strong>,
          },
        )
      : i18n._('Please check your phone for a code and enter it in the field below to log in.');

    return (
      <div style={loginContainerStyles}>
        <p style={styles.loginText}>{loginText}</p>
        <form
          method="POST"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            this.handleButtonClick(e);
          }}
        >
          <TextInputField
            autoComplete="off"
            focused
            hasError={!!this.state.codeValidationError}
            id="mfaCode"
            inputStyleOverrides={this.props.inputStyleOverrides}
            name="code"
            onChange={this.props.onCodeChange}
            placeholder={i18n._('Verification code')}
            tooltip={this.state.codeValidationError}
            type="text"
            value={this.props.code}
            useGestaltTooltip={this.props.useGestaltTooltip}
          />
          <Button
            accessibilityLabel={buttonText}
            className="red SignupButton"
            loading={this.state.loading}
            styleOverrides={styles.signupButton}
            type="submit"
          >
            <div>{buttonText}</div>
          </Button>
        </form>
        <div style={styles.loginFooterContainer}>
          {interpolateNamedTemplate(i18n._("If you didn't get it, we can {{ resendCode }}."), {
            resendCode: (
              <a onClick={this.handleResendCode.bind(this)} href="" key="code">
                {i18n._('resend the code')}
              </a>
            ),
          })}
        </div>
      </div>
    );
  }
}

LoginMfa.contextTypes = {
  isAuthenticated: PropTypes.bool.isRequired,
  locale: PropTypes.string.isRequired,
  loginState: PropTypes.number,
  hybridTier: PropTypes.string,
  pageType: PropTypes.string,
};

export default LoginMfa;
